import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import TaskService from '@/services/TaskService';
import type {
  TaskInstance,
  PaginatedClosedTasks,
  TaskHistory,
  TaskHistoryOpts,
} from '@/services/TaskService';
import { anyOpenWithdrawalRequest } from '@/logic/withdrawalRequest';
import { TASK_KEYS } from './keys';

// One template, every status — the pre-send confirmation's suppression lookup.
// staleTime 0: a stale "not booked yet" would preview a message WF17 won't send.
export function useTasksByTemplateCode(
  userid: string | number | null | undefined,
  templateCode: string | null | undefined,
) {
  return useQuery({
    queryKey: TASK_KEYS.byTemplateCode(userid ?? '', templateCode ?? ''),
    queryFn: () => TaskService.getTasksByTemplateCode(userid!, templateCode!),
    enabled: userid != null && userid !== '' && !!templateCode,
    staleTime: 0,
  });
}

// The patient's open withdrawal request: this period's first, else one left open on another period.
export function useOpenWithdrawalRequest(
  userid: string | number | null | undefined,
  carePeriodId: string | number | null | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: TASK_KEYS.withdrawalRequests(userid ?? ''),
    queryFn: () => TaskService.getWithdrawalRequests(userid!),
    select: (rows: TaskInstance[]) => anyOpenWithdrawalRequest(rows, carePeriodId),
    enabled: enabled && userid != null && userid !== '',
    staleTime: 0,
  });
}

// The patient's clinic-line call tasks, every status: what a monthly claim counted is re-checked against them.
export function useClinicCallTasks(
  userid: string | number | null | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: TASK_KEYS.clinicCalls(userid ?? ''),
    queryFn: () => TaskService.getClinicCallTasks(userid!),
    enabled: enabled && userid != null && userid !== '',
  });
}

// Imperative one-shot — fetch all tasks (open + recent closed) for a patient.
export function useFetchAllTasksForPatient(): (userid: string | number) => Promise<TaskInstance[]> {
  const qc = useQueryClient();
  return useCallback(
    (userid) => qc.fetchQuery({
      queryKey: TASK_KEYS.allForPatient(userid),
      queryFn: () => TaskService.getAllTasksForPatient(userid),
    }),
    [qc],
  );
}

// Imperative one-shot — fetch the OPEN tasks for a single patient (status-scoped,
// not the global queue).
export function useFetchOpenTasksForPatient(): (
  userid: string | number,
  opts?: { assignedToType?: string; includeLatent?: boolean }
) => Promise<TaskInstance[]> {
  const qc = useQueryClient();
  return useCallback(
    (userid, opts = {}) => qc.fetchQuery({
      queryKey: TASK_KEYS.openForPatient(userid, opts.assignedToType, opts.includeLatent),
      queryFn: () => TaskService.getOpenTasksForPatient(userid, opts),
      // staleTime:0 — this is the imperative "refresh now" path (post-create/-mutation).
      // Without it fetchQuery returns the cached list (global staleTime 5min) and a
      // freshly-created task never appears until the entry goes stale or the panel remounts.
      staleTime: 0,
    }),
    [qc],
  );
}

// Imperative one-shot — recency-sorted recent-closed tasks for a patient (bounded),
// for a "Done" preview of RECENT completions independent of the patient-file
// view's due_at-ordered, cap-limited task list.
export function useFetchRecentClosedTasksForPatient(): (
  userid: string | number,
  opts?: { offset?: number; limit?: number; assignedToType?: string }
) => Promise<PaginatedClosedTasks> {
  const qc = useQueryClient();
  return useCallback(
    (userid, opts = {}) => qc.fetchQuery({
      queryKey: TASK_KEYS.closedForPatient(userid, opts.assignedToType, opts.offset, opts.limit),
      queryFn: () => TaskService.getRecentClosedTasksForPatient(userid, opts),
    }),
    [qc],
  );
}

// Imperative one-shot for paginated load-more flows (TaskHistoryModal).
export function useFetchTaskHistory(): (
  userid: string | number,
  opts?: TaskHistoryOpts
) => Promise<TaskHistory> {
  const qc = useQueryClient();
  return useCallback(
    (userid, opts = {}) => qc.fetchQuery({
      queryKey: TASK_KEYS.history(userid, opts.closedOffset, opts.closedLimit, opts.assignedToType),
      queryFn: () => TaskService.getTaskHistory(userid, opts),
    }),
    [qc],
  );
}

export function useContentTasks(userid: string | number | null | undefined) {
  return useQuery({
    queryKey: TASK_KEYS.contentForPatient(userid ?? ''),
    queryFn: () => TaskService.getContentTasks(userid!),
    enabled: userid != null && userid !== '',
  });
}

export function useExceptionTasks(programid: string, periodIds: string[] | undefined) {
  return useQuery({
    queryKey: [...TASK_KEYS.all, 'exceptions', programid, periodIds],
    queryFn: () => TaskService.getOpenTasksForPeriods(periodIds!),
    enabled: periodIds !== undefined,
    staleTime: 0,
  });
}

export function useTaskQueue(roleFilter?: string | null) {
  return useQuery({
    queryKey: TASK_KEYS.queue(roleFilter),
    queryFn: () => TaskService.getTaskQueue(roleFilter),
  });
}

export type { TaskInstance, PaginatedClosedTasks, TaskHistory };
